import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import moment, { isMoment } from 'moment-timezone';
import TextArea from '../Base/TextArea/TextArea';
import TimePicker from '../Base/TimePicker/TimePicker';
import DatePicker from '../Base/DatePicker/DatePicker';
import Select from '../Base/Select/Select';
import PatientMiniProfile from '../PatientMiniProfile/PatientMiniProfile';
import TextField from '../Base/TextField/TextField';
import FormControl from '../Form/FormControl';
import SubmitButton from '../Button/SubmitButton';
import apiClient, { IResponse } from '../../Services/ApiClient';
import RemoveButton from '../Button/RemoveButton';
import useNotification from '../../Hooks/useNotification.hook';
import { useEffectAsync } from '../../helpers';
import Text from '../Base/Text/Text';
import FormWrapper from '../Form/FormWrapper';
import PatientTimeZoneInfo, {
	PatientTimeZoneInfoValues,
} from '../Patient/PatientTimeZoneInfo';
import { SelectValueProps } from '../Form/components/Select';
import {
	useLazyHealthPlansQuery,
	useLazyHealthPlanUserQuery,
	useLazyWorkplacePatientsQuery,
} from '../../graphql/graphql';
import { adjustDstBreakPoint, getMomentDate } from '../../Utils/dates.util';
import useTranslation from '../../Hooks/useTranslation';
import useWorkplaceRole from '../../Modules/Workplace/Hooks/useWorkplaceRole';
import { useAppContext } from '../../Services/AppContext';
import DoctorSelectField from '../Form/components/DoctorSelectField';
import Checkbox from '../Base/Checkbox/Checkbox';
import { useIsLatam } from '../../Modules/Region/Services/region.service';
import PatientSelect from '../../Modules/Patient/Components/PatientSelect/PatientSelect';
import { HealthPlanCode } from '../../Enums/HealthPlanCode.enum';
// @ts-ignore
import styles from './CalendarPatientEvent.module.scss';
import { WORKPLACE_CASE_TYPES_DOCTOR } from '../../Modules/Workplace/Graphql/workplace-case-types-doctor.graphql';
import useUserRelations, {
	HealthPlanRelation,
} from '../../Modules/User/Hooks/useUserRelations';
import { checkIsTimeSameOrAfter } from '../../Modules/Events/Services/eventInterval.service';
import InputField from '../Form/components/InputField';
import DateField from '../Form/components/DateField';
import InputWrapper from '../Form/components/InputWrapper/InputWrapper';
import BirthDatePickerField from '../../Modules/Base/Components/Inputs/BirthDatePicker/BirthDatePickerFiled';

interface ICalendarPatientEvent {
	selectedDate: any;
	patientId?: string;
	caseId?: string;
	onSuccess?: () => void;
	workplaceId?: string;
	order?: any;
	params?: any;
	onEventIsCreated?: (response: IResponse) => void;
	healthPlanCode?: HealthPlanCode;
}

const CalendarPatientEvent = (props: ICalendarPatientEvent) => {
	const {
		selectedDate,
		patientId,
		caseId,
		onSuccess,
		order,
		params,
		onEventIsCreated,
		healthPlanCode,
	} = props;
	const { t } = useTranslation();
	const { userData, globalTemplate, loggedUserId } = useAppContext();
	const { showNotificationSuccess, showNotificationAlert } = useNotification();

	const [isDefaultDoctor, setIsDefaultDoctor] = useState(false);
	const [newPatient, setNewPatient] = useState(false);
	const [searchPatients, setSearchPatients] = useState([]);
	const [workplaceCaseTypes, setWorkplaceCaseTypes] = useState([]);
	const [loadingWorkplaceCaseTypes, setLoadingWorkplaceCaseTypes] =
		useState(false);
	const [areHealthPlansLoading, setAreHealthPlansLoading] = useState(false);
	const [healthPlanOptions, setHealthPlanOptions] = useState<
		SelectValueProps[]
	>([]);
	const [loading, setLoading] = useState(!!order);
	const [toggleOther, setToggleOther] = useState(false);
	const [disabled, setDisabled] = useState(false);
	const [usersTimeDiff, setUsersTimeDiff] = useState(null);
	const [patientTimezone, setPatientTimezone] = useState(null);
	const [getHealthPlans] = useLazyHealthPlansQuery();
	const [getHealthPlanUser] = useLazyHealthPlanUserQuery();
	const isLatam = useIsLatam(globalTemplate);
	const { getHealthPlanDoctorId, getHealthPlanRelationCodeByHealthPlanCode } =
		useUserRelations();
	const [isCaseTypeSelectVisible, setIsCaseTypeSelectVisible] = useState(true);

	const workplaceId = props.workplaceId ?? useAppContext().workplaceId;

	const { id: userId, workplaceId: userWorkplaceId } = userData;

	const { isDoctor, isNurse } = useWorkplaceRole({
		userId,
		workplaceId: userWorkplaceId,
	});

	const isNurseView = !isDoctor && isNurse;

	const [defaultValues, setDefaultValues] = useState({
		date: selectedDate,
		timeFrom: selectedDate,
	});

	const [selectedPatientId, setSelectedPatientId] = useState(patientId);
	const [timeFrom, setTimeFrom] = useState(null);
	const [timeTo, setTimeTo] = useState(null);

	const [getWorkplacePatients] = useLazyWorkplacePatientsQuery();

	useEffectAsync(async () => {
		if (isNurse && patientId && healthPlanCode) {
			const healthPlanRelationCode: HealthPlanRelation | null =
				getHealthPlanRelationCodeByHealthPlanCode(healthPlanCode);
			if (healthPlanRelationCode) {
				const healthPlanDoctorId = await getHealthPlanDoctorId(
					patientId,
					workplaceId,
					healthPlanRelationCode,
				);
				// @ts-ignore
				formMethods.setValue('medicalProfessional', {
					value: healthPlanDoctorId,
				});
			}
		}
	});

	// @ts-ignore
	useEffectAsync(async () => {
		await handleLoadWorkplaceCaseTypes();
		if (patientId) {
			await handleSetPatientTimezone();
		}
	}, []);

	useEffect(() => {
		if (selectedPatientId || patientId) {
			handleLoadHealthPlans();
		}
	}, [selectedPatientId, patientId]);

	const isPeru = globalTemplate === 'peru';

	const validationSchema = yup.object({
		patientId:
			patientId || order?.event?.extendedProps.patient.id || newPatient
				? yup.mixed().notRequired()
				: yup.mixed().required(t('validation.calendarPatientEvent.patientId')),
		firstName: !newPatient
			? yup.mixed().notRequired()
			: yup.string().required(t('validation.calendarPatientEvent.firstName')),
		lastName: !newPatient
			? yup.mixed().notRequired()
			: isLatam
			? yup.mixed().notRequired()
			: yup.string().required(t('validation.calendarPatientEvent.lastName')),
		fatherLastName: !newPatient
			? yup.mixed().notRequired()
			: isLatam
			? yup
					.string()
					.required(
						t('validation.patientManualRegistrationLatamForm.fatherLastName'),
					)
			: yup.mixed().notRequired(),
		motherLastName: !newPatient
			? yup.mixed().notRequired()
			: isPeru
			? yup
					.string()
					.required(
						t('validation.patientManualRegistrationLatamForm.motherLastName'),
					)
			: yup.mixed().notRequired(),
		phoneNumber: !newPatient
			? yup.mixed().notRequired()
			: yup.string().required(t('validation.calendarPatientEvent.phoneNumber')),
		email: !newPatient
			? yup.mixed().notRequired()
			: yup.string().required(t('validation.calendarPatientEvent.email')),
		medicalProfessional: yup
			.object()
			.test(
				'empty-check',
				t('validation.calendarPatientEvent.medicalProfessional') ?? '',
				value => {
					return Boolean(value?.value);
				},
			)
			.required(t('validation.calendarPatientEvent.medicalProfessional')),
		...(isNurseView
			? {}
			: {
					caseType: isCaseTypeSelectVisible
						? yup
								.mixed()
								.required(t('validation.calendarPatientEvent.caseType'))
						: yup.mixed().notRequired(),
			  }),
		specification: !toggleOther
			? yup.mixed().notRequired()
			: yup
					.string()
					.required(t('validation.calendarPatientEvent.specification')),
		meetingType: yup
			.mixed()
			.required(t('validation.calendarPatientEvent.meetingType')),
		date: yup.date().required(t('validation.calendarPatientEvent.date')),
		timeFrom: yup
			.date()
			.nullable()
			.default(undefined)
			.required(t('validation.calendarPatientEvent.timeFrom')),
		timeTo: yup
			.date()
			.nullable()
			.default(undefined)
			.test(
				'is-greater',
				t('validation.calendarPatientEvent.timeDiff') ?? ' ',
				function () {
					const { date, timeFrom, timeTo } = this.parent;
					return checkIsTimeSameOrAfter(date, timeFrom, timeTo);
				},
			)
			.required(t('validation.calendarPatientEvent.timeTo')),
		note: yup.string().nullable().notRequired(),
		healthPlan: yup
			.object({
				label: yup.string(),
				value: yup.string(),
			})
			.nullable(),
	});

	const formMethods = useForm({
		defaultValues: defaultValues || {},
		resolver: yupResolver(validationSchema),
	});

	// @ts-ignore
	const handleLoadWorkplaceCaseTypes = async () => {
		setLoadingWorkplaceCaseTypes(true);

		const { data } = await apiClient.graphql({
			query: WORKPLACE_CASE_TYPES_DOCTOR,
			variables: {
				workplaceId: workplaceId,
				caseTypeTypes: ['CASE_TYPE'],
			},
			fetchPolicy: 'network-only',
		});

		const temp = [];

		for (const workplaceCaseType of data?.workplaceCaseTypesDoctor?.data ||
			[]) {
			if (workplaceCaseType.caseType.type === 'caseType') {
				temp.push({
					value: workplaceCaseType.id,
					label: workplaceCaseType.caseType.label,
					eventInterval: workplaceCaseType.caseType.eventInterval,
				});
			}
		}

		temp.push({
			value: 'other',
			label: t('base.other'),
			eventInterval: null,
		});

		setWorkplaceCaseTypes(temp);
		setLoadingWorkplaceCaseTypes(false);
	};

	const handleLoadHealthPlans = async () => {
		try {
			setAreHealthPlansLoading(true);

			const healthPlans = await getHealthPlans().unwrap();
			const rows = healthPlans?.healthPlans?.data || [];
			const healthPlanUsers = await Promise.all(
				rows.map(row =>
					getHealthPlanUser({
						userId: patientId ?? selectedPatientId,
						healthPlanCode: row?.code,
					}).unwrap(),
				),
			);

			const options = healthPlanUsers
				.filter(({ healthPlanUser }) => healthPlanUser?.isActive)
				.map(({ healthPlanUser }) => ({
					label: `${healthPlanUser?.healthPlan?.name}`,
					value: `${healthPlanUser?.healthPlan?.code}`,
					id: healthPlanUser?.id,
				}));

			setHealthPlanOptions(options);
			setAreHealthPlansLoading(false);
		} catch (error: any) {
			showNotificationAlert({
				content: error.response.data.message,
			});
		}
	};

	// @ts-ignore
	const handleCreate = async values => {
		setDisabled(true);

		let {
			date,
			timeFrom,
			timeTo,
			note,
			caseType,
			meetingType,
			medicalProfessional,
			phoneNumber,
			firstName,
			lastName,
			email,
			birthNumber,
			birthDate,
			specification,
			healthPlan,
			paidMeeting,
		} = values;
		let tempPatientId = patientId || values?.patientId?.value;

		const eventMembers = [];

		const birthDateInMoment = getMomentDate(birthDate);

		if (!tempPatientId) {
			const patientData: any = {
				firstName,
				lastName,
				phoneNumber,
				contactEmail: email,
				birthNumber: birthNumber?.length > 0 ? birthNumber : undefined,
				birthDate: birthDateInMoment.isValid()
					? birthDateInMoment.format('YYYY-MM-DD')
					: undefined,
			};

			if (isLatam) {
				patientData['middleName'] = values.middleName;
				patientData['fatherLastName'] = values.fatherLastName;
				patientData['motherLastName'] = values.motherLastName;
			}

			const workplaceMember = await apiClient.post(
				`/workplaces/${workplaceId}/patient`,
				{
					workplaceId,
					ignoreBirthDateCheck: !(birthDateInMoment.isValid() || !!birthNumber),
					patient: patientData,
				},
			);

			eventMembers.push({
				userId: workplaceMember.data.userId,
			});
		} else {
			if (tempPatientId) {
				eventMembers.push({
					userId: tempPatientId,
				});
			}
		}

		if (medicalProfessional?.value) {
			eventMembers.push({
				workplaceMemberId: medicalProfessional?.value,
			});
		}

		const meta = [];

		if (caseType && caseType.value != 'other') {
			meta.push({
				key: 'caseType',
				value: caseType.value,
			});
		}

		if (meetingType) {
			meta.push({
				key: 'meetingType',
				value: meetingType.value,
			});
		}

		if (params?.diabetesInitEvent) {
			meta.push({
				key: 'diabetesInitEvent',
				value: true,
			});
		}

		meta.push({
			key: 'paidMeeting',
			value: paidMeeting,
		});

		const day = moment(date);
		const timeFromMoment = getMomentDate(timeFrom);
		const timeToMoment = getMomentDate(timeTo);

		timeFrom = adjustDstBreakPoint(
			day.clone().set({
				hour: timeFromMoment.hours(),
				minute: timeFromMoment.minutes(),
				second: 0,
			}),
		);

		timeTo = adjustDstBreakPoint(
			day.clone().set({
				hour: timeToMoment.hours(),
				minute: timeToMoment.minutes(),
				second: 0,
			}),
		);

		if (healthPlanCode) {
			meta.push({
				key: 'healthPlanCode',
				value: HealthPlanCode,
			});
			meta.push({
				key: 'healthPlanUserItemKey',
				value: 'appointment1Month1',
			});
		}

		const response = await apiClient.post('/calendar/event/', {
			dateFrom: day
				.clone()
				.startOf('day')
				.add(timeFrom.format('HH'), 'hours')
				.add(timeFrom.format('mm'), 'minutes'), //moment(date).format("yyyy-MM-DD") + 'T' + moment(timeFrom).format('HH:mm:ss'),
			dateTo: day
				.clone()
				.startOf('day')
				.add(timeTo.format('HH'), 'hours')
				.add(timeTo.format('mm'), 'minutes'), // moment(date).format("yyyy-MM-DD") + 'T' + moment(timeTo).format('HH:mm:ss'),
			type: 'default',
			eventMembers,
			propIgnoreFreeTimeCheck: true,
			workplaceId: workplaceId,
			description: note ? note : '',
			caseId: caseId,
			title: specification ? specification : caseType?.label,
			meta,
			healthPlanUserId: healthPlan.id,
			ignoreMe: true,
		});

		if (onEventIsCreated) {
			onEventIsCreated(response);
		}

		if (response.data?.error) {
			setDisabled(false);
		} else {
			showNotificationSuccess({
				content: t('base.shared.patientOrdered'),
			});
			onSuccess && onSuccess();
		}
	};

	// @ts-ignore
	const handleRemove = async function () {
		await apiClient.delete('/events/cancel/' + order.event.id);
		onSuccess && onSuccess();
	};

	// @ts-ignore
	const handleUpdate = async function (values) {
		setDisabled(true);
		let {
			date,
			timeFrom,
			timeTo,
			patientId,
			note,
			caseType,
			meetingType,
			healthPlan,
			paidMeeting,
		} = values;
		const day = moment(date);
		timeFrom = getMomentDate(timeFrom);
		timeTo = getMomentDate(timeTo);

		const meta = [
			{
				key: 'meetingType',
				value: meetingType.value,
			},
			{
				key: 'paidMeeting',
				value: paidMeeting,
			},
		];

		if (caseType?.value) {
			meta.push({
				key: 'caseType',
				value: caseType?.value,
			});
		}

		const response = await apiClient.patch('/event/' + order.event.id, {
			dateFrom: day
				.clone()
				.startOf('day')
				.add(timeFrom.hours(), 'hours')
				.add(timeFrom.minutes(), 'minutes'), //moment(date).format("yyyy-MM-DD") + 'T' + moment(timeFrom).format('HH:mm:ss'),
			dateTo: day
				.clone()
				.startOf('day')
				.add(timeTo.hours(), 'hours')
				.add(timeTo.minutes(), 'minutes'), // moment(date).format("yyyy-MM-DD") + 'T' + moment(timeTo).format('HH:mm:ss'),
			eventMembers: [
				{
					userId: order?.event?.extendedProps.patient.id ?? patientId,
				},
				{
					workplaceMemberId: order?.event?.extendedProps?.doctor?.id,
				},
			],
			propIgnoreFreeTimeCheck: true,
			description: note ?? '',
			title: caseType?.label,
			workplaceId: workplaceId,
			meta: meta,
			healthPlanUserId: healthPlan?.id,
			ignoreMe: true,
		});

		if (!response?.data?.error) {
			onSuccess && onSuccess();
		}
		setDisabled(false);
	};

	useEffect(() => {
		if (!order) {
			return;
		}

		setLoading(true);

		const tempDefaultValues = {
			date: order?.event?.start ? getMomentDate(order.event.start) : undefined,
			timeFrom: order?.event?.start
				? getMomentDate(order.event.start)
				: undefined,
			timeTo: order?.event?.end ? getMomentDate(order.event.end) : undefined,
			note: order?.event?.extendedProps?.note,
		};

		if (order) {
			if (order?.event?.extendedProps?.meta) {
				const paidMeetingMeta = order?.event?.extendedProps?.meta?.find(
					m => m.key === 'paidMeeting',
				);

				if (paidMeetingMeta) {
					tempDefaultValues['paidMeeting'] = paidMeetingMeta.value === true;
				}

				const caseTypeMeta = order.event.extendedProps.meta.find(
					m => m.key === 'caseType',
				);

				if (caseTypeMeta) {
					tempDefaultValues['caseType'] = workplaceCaseTypes.find(
						p => p.value === caseTypeMeta.value,
					);
					setToggleOther(false);
				} else {
					tempDefaultValues['caseType'] = 'other';
					tempDefaultValues['specification'] = order?.event?.title;
					setToggleOther(true);
				}

				const meetingTypeMeta = order.event.extendedProps.meta.find(
					m => m.key === 'meetingType',
				);

				if (meetingTypeMeta) {
					switch (meetingTypeMeta.value) {
						case 'telemedicine':
							tempDefaultValues['meetingType'] = {
								value: 'telemedicine',
								label: t('base.meetingType.telemedicine'),
							};
							break;
						case 'onSite':
							tempDefaultValues['meetingType'] = {
								value: 'onSite',
								label: t('base.meetingType.onSite'),
							};
							break;
					}
				}
			}
		}

		setTimeFrom(tempDefaultValues.timeFrom);
		setTimeTo(tempDefaultValues.timeTo);
		setDefaultValues(tempDefaultValues);

		formMethods.reset(tempDefaultValues);

		setLoading(false);
	}, [order, workplaceCaseTypes]);

	const handleSearchPatients = value => {
		// @ts-ignore
		const a = async () => {
			setSearchPatients([]);

			if (!value) {
				return;
			}

			const { workplacePatients } = await getWorkplacePatients({
				fulltext: value,
				workplaceId,
			}).unwrap();

			setSearchPatients(workplacePatients?.data || []);
		};
		a();
	};
	// @ts-ignore
	const handleCalculateTimeTo = async values => {
		if (!formMethods) {
			return;
		}
		const formValues = formMethods.getValues();

		if (values.value === 'other') {
			setToggleOther(true);
		} else {
			//@ts-ignore
			formMethods.setValue('specification', undefined);
			setToggleOther(false);
		}

		if (values?.eventInterval && values?.eventInterval > 0) {
			formMethods.setValue(
				'timeTo',
				formValues.timeFrom.clone().add(values.eventInterval, 'minutes'),
			);
		}
	};

	// @ts-ignore
	const handleSetPatientTimezone = () => {
		if (!formMethods) {
			return;
		}
		const formValues = formMethods.getValues();
		setSelectedPatientId(patientId ?? formValues?.patientId?.value);
		setTimeFrom(formValues.timeFrom);
		setTimeTo(formValues.timeTo);
	};

	// @ts-ignore
	const handleGetPatientsTimes = () => {
		if (!formMethods) {
			return;
		}
		const formValues = formMethods.getValues();

		setTimeFrom(formValues.timeFrom);
		setTimeTo(formValues.timeTo);
	};

	const handlePatientTimeDiffChange = (change: PatientTimeZoneInfoValues) => {
		setUsersTimeDiff(change.timeDiff);
		setPatientTimezone(change.patientTimezone);
	};

	useEffect(() => {
		if (order?.event?.extendedProps?.doctor?.id) {
			//@ts-ignore
			formMethods.setValue('medicalProfessional', {
				value: order.event.extendedProps.doctor.id,
				label: order?.event?.extendedProps?.doctor?.name,
			});
		} else {
			if (isDoctor) {
				//@ts-ignore
				formMethods.setValue('medicalProfessional', { value: userId });
			}
		}
	}, [userId, isDoctor, isDefaultDoctor]);

	const healthPlanValue = formMethods.watch('healthPlan')?.value;
	useEffect(() => {
		if (healthPlanValue && healthPlanValue != 'withoutHealthPlan') {
			setIsCaseTypeSelectVisible(false);
		} else {
			setIsCaseTypeSelectVisible(true);
		}
	}, [healthPlanValue]);

	useEffect(() => {
		const eventsWithInvisibleCaseType: string[] = [
			'meddibaby_appointment',
			'meddibaby_first_appointment',
			'diabetes_appointment',
			'diabetes_first_appointment',
		];
		for (const invisibleItemKey of eventsWithInvisibleCaseType) {
			if (
				order?.event?.extendedProps?.meta?.some(
					(item: any) => item.key === invisibleItemKey,
				)
			) {
				setIsCaseTypeSelectVisible(false);
			}
		}
	}, [order]);

	return (
		<>
			{!loading && (
				<FormWrapper formMethods={formMethods}>
					{patientId || order?.event?.extendedProps.patient.id ? (
						<div className={'mb-4'}>
							<PatientMiniProfile
								userId={order?.event?.extendedProps?.patient?.id ?? patientId}
								workplaceId={workplaceId}
								loggedUserId={loggedUserId}
								showVerifyInformation={false}
								links={null}
								showAge={true}
								showBirthDate={false}
								showAddress={false}
								showPhone={false}
								showEmail={false}
								showInsuranceCompany={false}
								showGender={false}
							/>
						</div>
					) : !newPatient ? (
						<div className={'mb-3'}>
							<FormControl
								label='base.shared.searchPatient'
								name='patientId'
								onChange={handleSetPatientTimezone}
								isFormContextDisabled
								isRequired={true}>
								<PatientSelect workplaceId={workplaceId} />
							</FormControl>
							<button
								onClick={() => setNewPatient(true)}
								className={'btn-link blue text-underline'}>
								{t('component.searchingPatients.patientNotRegistered')}
							</button>
							<hr />
						</div>
					) : (
						<div className={'mb-3'}>
							{isLatam ? (
								<NewPatientLatamFields isPeru={isPeru} />
							) : (
								<NewPatientFields />
							)}
							<div className={'row'}>
								<div className={'col-6'}>
									<FormControl
										label='base.shared.phoneNumber'
										name='phoneNumber'
										isRequired={true}>
										<TextField />
									</FormControl>
								</div>
								<div className={'col-6'}>
									<FormControl
										label='base.shared.email'
										name='email'
										isFormContextDisabled
										isRequired={true}>
										<TextField />
									</FormControl>
								</div>
							</div>
							<div className={'row'}>
								<div className={'col-12'}>
									<InputWrapper>
										<BirthDatePickerField
											name='birthDate'
											label='base.shared.dateOfBirth'
										/>
									</InputWrapper>
								</div>
							</div>
							<div className={'row'}>
								<div className={'col-6'}>
									<InputWrapper>
										<InputField name='birthNumber' label='base.birthNumber' />
									</InputWrapper>
								</div>
							</div>
							<hr />
						</div>
					)}
					<div>
						<div style={{ marginBottom: '10px' }}>
							<DoctorSelectField
								isRequired
								name='medicalProfessional'
								label='base.professionalName'
								roleKeys={['doctor', 'therapist', 'nurse']}
								approved
							/>
						</div>
						{selectedPatientId &&
							!areHealthPlansLoading &&
							healthPlanOptions.length > 0 && (
								<FormControl name='healthPlan'>
									<Select
										label='component.appointment.pickHealthPlan'
										placeholder={t('base.shared.pick')}
										options={[
											...[
												{
													label: t('base.withoutHealthPlan'),
													value: 'withoutHealthPlan',
												},
											],
											...healthPlanOptions,
										]}
									/>
								</FormControl>
							)}
						{!loadingWorkplaceCaseTypes && isCaseTypeSelectVisible && (
							<FormControl
								label='base.pickCaseType'
								name='caseType'
								isFormContextDisabled
								onChange={handleCalculateTimeTo}
								isRequired={true}>
								<Select
									placeholder={t('base.shared.pick')}
									options={workplaceCaseTypes}
								/>
							</FormControl>
						)}
						{toggleOther && (
							<FormControl
								label='base.specification'
								isFormContextDisabled
								name='specification'
								isRequired={true}>
								<TextField />
							</FormControl>
						)}
						<FormControl
							label='base.typeOfEvent'
							name='meetingType'
							isFormContextDisabled
							isRequired={true}>
							<Select
								placeholder={t('base.shared.pick')}
								options={[
									{
										value: 'telemedicine',
										label: t('base.meetingType.telemedicine'),
									},
									{
										value: 'onSite',
										label: t('base.meetingType.onSite'),
									},
								]}
							/>
						</FormControl>
						{!!usersTimeDiff && (
							<div className={styles.warning}>
								<Text
									tx={'base.patientsIsInTimeZone.part1'}
									inline
									firstUpper
								/>{' '}
								({patientTimezone}){', '}
								<span className={styles.warningBold}>{usersTimeDiff}</span>{' '}
								<Text
									tx={'base.shared.hourMiddle'}
									className={styles.warningBold}
									inline
								/>{' '}
								<Text tx={'base.patientsIsInTimeZone.part2'} inline />.
							</div>
						)}
						<div className={'row'}>
							<div className={'col-4'}>
								<FormControl
									label='base.shared.date'
									name='date'
									isFormContextDisabled
									isRequired={true}>
									<DatePicker minDate={moment()} />
								</FormControl>
							</div>
							<div className={'col-4'}>
								<FormControl
									label='base.shared.from'
									name='timeFrom'
									isFormContextDisabled
									onChange={handleGetPatientsTimes}
									isRequired={true}>
									<TimePicker />
								</FormControl>
								<PatientTimeZoneInfo
									patientId={selectedPatientId}
									time={timeFrom}
									onChange={handlePatientTimeDiffChange}
								/>
							</div>
							<div className={'col-4'}>
								<FormControl
									label='base.shared.to'
									name='timeTo'
									isFormContextDisabled
									onChange={handleGetPatientsTimes}
									isRequired={true}>
									<TimePicker />
								</FormControl>
								<PatientTimeZoneInfo
									patientId={selectedPatientId}
									time={timeTo}
								/>
							</div>
						</div>
						<FormControl
							label='base.shared.noteVisibleForPatient'
							name='note'
							isFormContextDisabled>
							<TextArea />
						</FormControl>
						{isLatam && (
							<FormControl name='paidMeeting'>
								<Checkbox tx='component.calendarPatientEvent.paidMeeting' />
							</FormControl>
						)}
						<div
							className={'d-flex flex-row align-items-center mt-4'}
							style={{ gap: '15px' }}>
							{!order ? (
								<SubmitButton
									onSubmit={handleCreate}
									title='component.eventDetail.saveToCalendarAndSendInfoToPatient'
									disabled={disabled}
								/>
							) : (
								<>
									<SubmitButton
										onSubmit={handleUpdate}
										title={'component.calendar.context.edit'}
										disabled={disabled}
									/>
									<div className={'ml-3'}>
										<RemoveButton onClick={handleRemove} />
									</div>
								</>
							)}
						</div>
					</div>
				</FormWrapper>
			)}
		</>
	);
};

export default CalendarPatientEvent;

interface INewPatientLatamFields {
	isPeru?: boolean;
}

const NewPatientLatamFields: React.FC<INewPatientLatamFields> = ({
	isPeru = false,
}) => {
	const { globalTemplate } = useAppContext();

	return (
		<div className={'row'}>
			<div className='col-6 mb-3'>
				<InputField
					name='firstName'
					label='diabetes.patientManualRegistration.firstName'
					isRequired
				/>
			</div>
			<div className='col-6 mb-3'>
				<InputField
					name='middleName'
					label='diabetes.patientManualRegistration.secondName'
				/>
			</div>
			<div className='col-6 mb-3'>
				<InputField
					name='fatherLastName'
					label='diabetes.patientManualRegistration.fathersName'
					isRequired
				/>
			</div>
			<div className='col-6 mb-3'>
				<InputField
					name='motherLastName'
					label='diabetes.patientManualRegistration.mothersName'
					isRequired={isPeru}
				/>
			</div>
			<div className='col-6 mb-3'>
				<InputField
					name='lastName'
					label='diabetes.patientManualRegistration.marriageName'
				/>
			</div>
		</div>
	);
};
const NewPatientFields: React.FC = () => {
	return (
		<div className={'row'}>
			<div className={'col-6'}>
				<FormControl
					label='base.shared.firstName'
					name='firstName'
					isFormContextDisabled
					isRequired={true}>
					<TextField />
				</FormControl>
			</div>
			<div className={'col-6'}>
				<FormControl
					label='base.shared.lastName'
					name='lastName'
					isFormContextDisabled
					isRequired={true}>
					<TextField />
				</FormControl>
			</div>
		</div>
	);
};
